/***
 * This file contains customization-related types that are used across different domain types (contour, dose, etc)
 */

import i18next from "i18next";
import { alphabeticalSort } from "../../util/sort";
import { getVersionNumbers } from "../../util/string-convert";
import { ImageDicomTag } from "../image/image-types";


/** Visibility of a model */
export enum ModelVisibility {
    Default = 'DefaultVisibility',
    AlwaysShown = 'AlwaysShown',
    AlwaysHidden = 'AlwaysHidden'
}

/** DICOM modalities */
export enum Modality {
    CT = 'CT',
    MR = 'MR',
    PET = 'PET',
    RTSTRUCT = 'RTSTRUCT',
    RTDOSE = 'RTDOSE',
    RTPLAN = 'RTPLAN',
    RTIMAGE = 'RTIMAGE',
}

/** Supported modalities as a string array */
export const SUPPORTED_MODALITIES = Object.values(Modality).map(m => m.toString());

/** Direction/type of modality (input or output) */
export enum ModalityDirection { Input = 'Input', Output = 'Output' }


/** Name of 'default' base customization */
export const DEFAULT_MODEL_CUSTOMIZATION_NAME = 'default';

/** The 'action' trigger used when a trigger is a DICOM rule instead of an AE Title rule. */
export const DICOM_RULE_TRIGGER_ACTION = 'seg';

/** Name of the filename metadata attribute. */
export const METADATA_FILENAME_ATTRIBUTE = 'filename';

export const DEFAULT_SUBSCRIPTION = 'default';

/** Model type for Contour+ models. */
export const CONTOUR_MODEL_TYPE = 'segmentation';

/** Model type for Dose+ prediction models. */
export const DOSE_MODEL_TYPE = 'dose';

/** Model type for Image+ models. */
export const IMAGE_MODEL_TYPE = 'translation';

/** Model type for Adapt+ models. */
export const ADAPT_MODEL_TYPE = 'registration';

/** Type (algorithm) of a model. */
export enum ModelType { None = 'None', Adapt = 'Adapt', Contouring = 'Contouring', Dose = 'Dose', Image = 'Image' };

export enum ModelSelectionScope { None, ModelRules, AeTitleRule, DicomRule, DicomAttributeRule, All };

/** Unit that has been selected for dose in a customization. */
export enum SelectedDoseUnit { Gy = 'Gy', cGy = 'cGy', NotSet = 'not_set' };

export const DEFAULT_DOSE_UNIT = SelectedDoseUnit.Gy;

export type CustomizationBaseWithModelName = { customizationBaseId: string, label: string };

export const DEFAULT_INTERPRETED_TYPE = 'ORGAN';


/** Tag used to mark a latest version of a model */
export const TAG_LATEST = 'latest';



/** Tri-state validation status for sidebar tree view items. */
export enum ValidationStatus {
    Valid = 'Valid',
    Warning = 'Warning',
    Error = 'Error',
}

/** Returns the worse of two validation statuses (Error > Warning > Valid). */
export const worstValidationStatus = (a: ValidationStatus, b: ValidationStatus): ValidationStatus => {
    if (a === ValidationStatus.Error || b === ValidationStatus.Error) { return ValidationStatus.Error; }
    if (a === ValidationStatus.Warning || b === ValidationStatus.Warning) { return ValidationStatus.Warning; }
    return ValidationStatus.Valid;
}

/** Type of a customization view model object. For helper function use only. */
export enum CustomizationObjectType {
    None = "NONE",
    Model = "MODEL",
    CustomizationBase = "CUSTOMIZATION_BASE",
    CustomizationOutput = "CUSTOMIZATION_OUTPUT",
    Metadata = "METADATA",
    Roi = "ROI",
    Target = "TARGET",
    GlobalRoi = "GLOBAL_ROI",
    AeTitleRule = "AE_TITLE_RULE",
    DicomRule = "DICOM_RULE",
    DicomAttributeRule = "DICOM_ATTRIBUTE_RULE",
    TriggerRule = "TRIGGER_RULE",
    CodingScheme = "CODING_SCHEME",
    DicomRestriction = "DICOM_RESTRICTION",
    OutputGeometry = "OUTPUT_GEOMETRY",
    ContourGeneration = "CONTOUR_GENERATION",
    PostProcessing = "POST_PROCESSING",
    DicomTag = "DICOM_TAG",
    KeepLargestComponent = "KEEP_LARGEST_COMPONENT",
    FillHoles = "FILL_HOLES",
    BodyMask = "BODY_MASK",
    HoleMask = "HOLE_MASK",
    AdaptRoiRule = "ADAPT_ROI_RULE",


    /** TODO: this is currently used ONLY for reading backend error messages */
    PhysicalProperties = 'PHYSICAL_PROPERTIES',
};

/** Helper type for constructing hierarchical tree views from customization data. Since these objects are
 * recursive and not flat it's best to avoid storing them in redux store.
 */
export type CustomizationTreeViewDataItem = {
    /** ID of this item. */
    id: string,
    /** Human-readable label of this item. */
    label: string,
    /** Recursive children of this item. */
    children: CustomizationTreeViewDataItem[],
    /** Soft link to parent object */
    parentId: string | undefined,
    /** The type that this item models. */
    type: CustomizationObjectType,
    // /** If defined, this ID will be used instead of this object's actual ID when performing actions on this item (e.g. "opening" it in UI).
    //  * Should match a given actionType. */
    // actionId?: string,
    // /** If defined, this CustomizationObjectType will be used instead of this object's actual type when performing actions on this item
    //  *  (e.g. "opening" it in UI). Should match a given actionId. */
    // actionType?: CustomizationObjectType,
    isModified: boolean,
    /** Validation status of this object and all its descendants: Valid, Warning, or Error. */
    validationStatus: ValidationStatus,
    /** True if this object is available for user use in the actual product, false otherwise. */
    isAvailable: boolean,
    /** True if this object is in the deprecated list, false otherwise. */
    isDeprecated: boolean,
    /** True if this object is the latest version, false otherwise. */
    isLatest: boolean,
    /** Model visibility of this item. Mainly applicable for models, other customization object types are not guaranteed to follow this visibility setting. */
    modelVisibility: ModelVisibility,
    /** List of tags assigned to this item. Generally only used for top-level model items. */
    tags?: string[],
    /** Version number of this item, if applicable. */
    version?: string,
    /** Input modalities supported by this item. Currently only used with ContourModelSelect component data. */
    inputModalities?: Modality[],
    /** Output modalities supported by this item. Currently only used with ContourModelSelect component data. */
    outputModalities?: Modality[],
}

/** One of currently two supported physical properties: REL_ELEC_DENSITY */
export const PHYSICAL_PROPERTY_REL_ELEC_DENSITY = 'REL_ELEC_DENSITY';

/** One of currently two supported physical properties: REL_MASS_DENSITY */
export const PHYSICAL_PROPERTY_REL_MASS_DENSITY = 'REL_MASS_DENSITY';

export const SUPPORTED_PHYSICAL_PROPERTIES = [PHYSICAL_PROPERTY_REL_ELEC_DENSITY, PHYSICAL_PROPERTY_REL_MASS_DENSITY];

/** Currently supported physical property attribute names. */
export enum PhysicalProperty { NotSet = 'NotSet', RelElecDensity = 'REL_ELEC_DENSITY', RelMassDensity = 'REL_MASS_DENSITY' };

/** Parent type for modelling a contouring/dose/etc model and customization and selection/trigger rules that are related to it. */
export type Model = {
    /** Redux store ID of this model. */
    id: string;
    /** Type (algorithm) of this model. */
    modelType: ModelType;
    /** Name of the model. */
    modelName: string;
    /** Human-friendly label of the model. */
    label: string;
    /** Body part that this model covers. */
    bodyPart: string;
    /** An optional description of the model. */
    description: string;
    /** Visibility of this model: default, always shown, or always hidden. */
    visibility: ModelVisibility;
    /** Version number of the model. */
    version: string;
    /** Tags related to this model. These should be pre-sorted during conversion from JSON to view models into following order:
     *  modalities > the rest of the tags in alphabetical order.
     */
    tags: string[];
    /** Input modalities supported by this model. */
    inputModalities: Modality[];
    /** Output modalities supported by this model. */
    outputModalities: Modality[];

    /** Customizations and outputs that belong to this model. */
    customizations: string[]; // ModelCustomizationBase ids

    /** True if this model has unsaved changes, false otherwise. */
    isModified: boolean;

    /** True if this model is available for user to use in the actual product, false otherwise. **/
    isAvailable: boolean;

    /** True if model is in the deprecated list, false otherwise. **/
    isDeprecated: boolean;

    /** True if this is the latest available version for this model, false otherwise. */
    isLatest: boolean;

    /** Unix timestamp (in seconds) of when this model was last saved/modified on the backend. */
    lastModifiedTimestamp?: number;
};

/** A single model customization. One model customization can end up having multiple outputs (ie. result files). */
export type CustomizationBase = {
    /** Redux store ID of this customization base. */
    id: string;
    /** Name for this customization. */
    customizationName: string;
    /** An optional description of this customization base. */
    description: string;

    /** One or more outputs (ie. result files) that belong to this customization base. */
    outputs: string[]; // ModelCustomizationOutput ids

    /** Selection rules that trigger this customization. */
    aeTitleRules: string[];  // AeTitleRule ids
    dicomRules: string[];  // DicomRule ids

    /** Parent model ID of this customization base. */
    modelId: string;

    /** True if this customization base has unsaved changes, false otherwise. */
    isModified: boolean;
};

/** A single customization output (ie. a result file). A customization base contains one or more of these. Most domain types (contouring, dose, etc) have
 * their own versions of these.
 */
export type CustomizationOutput = {
    /** Redux store ID of this customization output. */
    id: string;

    /** Shorthand access to this output's filename metadata entry (an empty string if not defined). This is only used
     * for user-facing UI purposes -- the matching metadata entry is still the single source of truth for this attribute. */
    filename: string;

    /** Customization metadata relevant to this customization output. */
    metadata: string[]; // ModelCustomizationMetadataItem ids

    /** Parent customization base ID of this output. */
    modelCustomizationBaseId: string;

    /** True if this customization output has unsaved changes, false otherwise. */
    isModified: boolean;
};

/** A single customization output metadata item. */
export type OutputMetadataItem = {
    /** Redux ID of this customization metadata item. */
    id: string;
    /** Name of the metadata attribute. */
    attribute: string;
    /** Value of the metadata attribute. */
    value: string;

    /** True if this metadata item has unsaved changes, false otherwise. */
    isModified: boolean;
    /** If true, scroll the viewport to show this item when it's mounted into DOM. This is used to auto-scroll to newly added items. */
    scrollToView: boolean;

    /** Parent model customization output this metadata item belongs to. */
    modelCustomizationOutputId?: string;   // this is optional because it's usually set post-constructor
}


/** Base type for a model selection/trigger rule. */
type RuleBase = {
    /** Redux store ID for this rule. */
    id: string;

    /** Name of the subscription that this rule belongs to. */
    subscription: string;

    /** The model customization that this rule will trigger. This should match whatever the customizationName is set to. */
    modelCustomizationBaseId?: string;  // this is optional because it's usually set post-constructor

    aggregation: string | null;

    /** If true this rule can be edited by users. If false it's considered built-in and cannot be adjusted. */
    isEditable: boolean;
    /** If true this rule is highlighted in UI as being "new". */
    isNew: boolean;
    /** True if this rule has unsaved changes, false otherwise. */
    isModified: boolean;
}

/** Model selection/trigger rule that is triggered from a specific AE Title or backend API value. */
export type AeTitleRule = RuleBase & {
    /** Value of the AE Title or backend API action field that will trigger this rule. */
    action: string;
}

/** Model selection/trigger rule that is triggered from specific DICOM attribute values. */
export type DicomRule = RuleBase & {
    /** List of DICOM attributes and values that, on match, will trigger this rule. */
    dicomAttributes: string[]; // DicomAttributeRule ids
}

/** A DICOM attribute name and value set that can be used to trigger a DICOM rule. */
export type DicomAttributeRule = {
    /** Redux store ID of this DICOM attribute rule. */
    id: string;
    /** Name of the matching DICOM attribute that will trigger this rule. */
    attribute: string;
    /** Value (either direct or regexed) of the matching DICOM attribute that will trigger this rule. */
    value: string;
    /** Id of the DICOM rule this attribute belongs to. */
    parentDicomRuleId: string;

    /** If true this rule is highlighted in UI as being "new". */
    isNew: boolean,
    /** True if this rule has unsaved changes, false otherwise. */
    isModified: boolean;
}

/** A model can be triggered by one or more AE Title rules and/or DICOM rules. */
export type ModelTrigger = AeTitleRule | DicomRule;


// *** type guards ***

export const isModel = (obj: unknown): obj is Model => {
    const model = obj as Model;
    return !!model && model.modelName !== undefined && model.customizations !== undefined;
}

export const isCustomizationBase = (obj: unknown): obj is CustomizationBase => {
    const customization = obj as CustomizationBase;
    return !!customization
        && customization.customizationName !== undefined && customization.outputs !== undefined
        && customization.aeTitleRules !== undefined && customization.dicomRules !== undefined
        && customization.modelId !== undefined;
}

export const isCustomizationOutput = (obj: unknown): obj is CustomizationOutput => {
    const output = obj as CustomizationOutput;
    return !!output
        && output.metadata !== undefined
        && output.modelCustomizationBaseId !== undefined
        && output.filename !== undefined;
}

export const isOutputMetadata = (obj: unknown): obj is OutputMetadataItem => {
    const metadata = obj as OutputMetadataItem;
    return !!metadata
        && metadata.attribute !== undefined && metadata.value !== undefined
        // ensure that the metadata object does NOT have the parentDicomRuleId as otherwise metadata and dicom attribute rule types are very similar
        && (obj as DicomAttributeRule).parentDicomRuleId === undefined
        // ensure that the metadata object does not look like the very similar imageDicomTag either
        && (obj as ImageDicomTag).dicomRestrictionId === undefined;
}

const isRuleBase = (obj: unknown): obj is RuleBase => {
    const ruleBase = obj as RuleBase;
    return !!ruleBase && ruleBase.subscription !== undefined;
}

export const isAeTitleRule = (obj: unknown): obj is AeTitleRule => {
    const aeTitleRule = obj as AeTitleRule;
    return !!aeTitleRule && isRuleBase(obj) && aeTitleRule.action !== undefined && (obj as DicomRule).dicomAttributes === undefined;
}

export const isDicomRule = (obj: unknown): obj is DicomRule => {
    const dicomRule = obj as DicomRule;
    return !!dicomRule && isRuleBase(obj) && dicomRule.dicomAttributes !== undefined;
}

export const isDicomAttributeRule = (obj: unknown): obj is DicomAttributeRule => {
    const dicomAttributeRule = obj as DicomAttributeRule;
    return !!dicomAttributeRule
        && dicomAttributeRule.attribute !== undefined
        && dicomAttributeRule.value !== undefined
        && dicomAttributeRule.parentDicomRuleId !== undefined;
}

/** Converts a ModelType enum into a string for the /config/export endpoint.
 *  Note: Contouring maps to 'contouring' here (not 'segmentation' as in convertModelTypeToApiString). */
export const convertModelTypeToExportApiString = (modelType: ModelType): string => {
    switch (modelType) {
        case ModelType.Contouring:
            return 'contouring';
        case ModelType.Dose:
            return DOSE_MODEL_TYPE;
        case ModelType.Image:
            return IMAGE_MODEL_TYPE;
        case ModelType.Adapt:
            return ADAPT_MODEL_TYPE;
        default:
            throw new Error(`Unsupported model type for export: ${modelType}`);
    }
}

/** Converts a ModelType enum into a string that can be sent to a backend API endpoint */
export const convertModelTypeToApiString = (modelType: ModelType) => {
    let modelTypeHeader = '';
    switch (modelType) {
        case ModelType.Contouring:
            modelTypeHeader = CONTOUR_MODEL_TYPE;
            break;
        case ModelType.Dose:
            modelTypeHeader = DOSE_MODEL_TYPE;
            break;
        case ModelType.Adapt:
            modelTypeHeader = ADAPT_MODEL_TYPE;
            break;
        case ModelType.Image:
            modelTypeHeader = IMAGE_MODEL_TYPE;
            break;
        default:
            throw new Error(`Unsupported model type: ${modelType}`);
    }

    return modelTypeHeader;
}

/** Returns the user-facing display label for a model type (e.g. "Contour+", "Dose+"). */
export const getModelTypeDisplayLabel = (modelType: ModelType): string => {
    switch (modelType) {
        case ModelType.Contouring:
            return i18next.t('translation:common.modelType.contour');
        case ModelType.Dose:
            return i18next.t('translation:common.modelType.dose');
        case ModelType.Adapt:
            return i18next.t('translation:common.modelType.adapt');
        case ModelType.Image:
            return i18next.t('translation:common.modelType.image');
        default:
            throw new Error(`Unsupported model type: ${modelType}`);
    }
}

/** Returns a CSS tag for model type. Generally only used for module-specific style overrides. */
export const getModelTypeStyle = (modelType: ModelType): string => {
    switch (modelType) {
        case ModelType.Contouring:
            return 'mv-contour';
        case ModelType.Dose:
            return 'mv-dose';
        case ModelType.Adapt:
            return 'mv-adapt';
        case ModelType.Image:
            return 'mv-image';
        default:
            throw new Error(`Unsupported model type: ${modelType}`);
    }
}

/** Returns a model's tags in an uniform sort: 'latest' > version numbers in descending order > modalities > other tags in
 * alphabetical order.
 */
export const getModelTagsInOrder = (modelTags: string[]): string[] => {
    const tags: string[] = [];

    // always first use 'latest' if present
    if (modelTags.includes(TAG_LATEST)) {
        tags.push(TAG_LATEST);
    }

    // then find tags that look like version numbers and sort them in descending order
    const versionTags = getVersionNumbers(modelTags);
    versionTags.sort((a, b) => {
        const aParts = a.split('.').map(Number);
        const bParts = b.split('.').map(Number);

        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
            const aPart = aParts[i] || 0; // default to 0 if part is missing
            const bPart = bParts[i] || 0; // default to 0 if part is missing
            if (aPart !== bPart) {
                return bPart - aPart;
            }
        }
        return 0; // they are equal
    });
    tags.push(...versionTags);

    // then find modalities
    const modalityTags = modelTags.filter(t => !tags.includes(t)).filter(t => SUPPORTED_MODALITIES.includes(t));

    // sort modalityTags in the order Modality enums are defined
    modalityTags.sort((a, b) => {
        const modalities = Object.values(Modality);
        return modalities.indexOf(a as Modality) - modalities.indexOf(b as Modality);
    });

    tags.push(...modalityTags);

    // finally sort and add the rest of the tags
    alphabeticalSort(modelTags.filter(t => !tags.includes(t))).map(tag => tags.push(tag));

    return tags;
}
